import { ChevronRight, Search } from "lucide-react";
import type { ReactNode } from "react";
import { CatalogBadges } from "@/components/agents/catalog-badges";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export interface CatalogPickerItem {
	key: string;
	name: string;
	description: string;
	badges: string[];
}

/** One-row-per-item catalog skeleton. */
function CatalogCardGridSkeleton({ count = 4 }: { count?: number }) {
	return (
		<div
			className="grid grid-cols-1 gap-2"
			role="status"
			aria-label="Loading catalog"
		>
			{Array.from({ length: count }).map((_, i) => (
				<Skeleton
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
					key={i}
					className="h-16 w-full rounded-lg"
				/>
			))}
		</div>
	);
}

export function ConnectorCatalogListSkeleton({
	count = 8,
}: {
	count?: number;
}) {
	return (
		<div
			className="overflow-hidden rounded-lg border"
			role="status"
			aria-label="Loading connector catalog"
		>
			{Array.from({ length: count }).map((_, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
					key={i}
					// bg-card matches the loaded rows' surface, so resolving the
					// list swaps content without repainting the background.
					className={`flex items-center gap-3 bg-card px-3 py-2.5 ${
						i === count - 1 ? "" : "border-b"
					}`}
				>
					<Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
					<div className="min-w-0 flex-1 space-y-1.5">
						<Skeleton className="h-3.5 w-24 rounded" />
						<Skeleton className="h-3 w-48 max-w-full rounded" />
					</div>
					<Skeleton className="h-4 w-14 shrink-0 rounded-full" />
					<Skeleton className="h-4 w-4 shrink-0 rounded" />
				</div>
			))}
		</div>
	);
}

/** Catalog search + card grid while template/provider/skill catalogs load. */
export function CatalogPickerSkeleton({
	count = 4,
	withSearch = true,
}: {
	count?: number;
	withSearch?: boolean;
}) {
	return (
		<div className="space-y-3" role="status" aria-label="Loading catalog">
			{withSearch ? <Skeleton className="h-9 w-full rounded-md" /> : null}
			<CatalogCardGridSkeleton count={count} />
		</div>
	);
}

export function CatalogPicker({
	items,
	searchQuery,
	onSearchChange,
	onSelect,
	emptyMessage,
	searchPlaceholder = "Search...",
	searchAriaLabel = "Search catalog",
	renderTitle,
}: {
	items: CatalogPickerItem[];
	searchQuery: string;
	onSearchChange: (value: string) => void;
	onSelect: (item: CatalogPickerItem) => void;
	emptyMessage?: string;
	searchPlaceholder?: string;
	searchAriaLabel?: string;
	renderTitle?: (item: CatalogPickerItem) => ReactNode;
}) {
	return (
		<div className="space-y-3">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					aria-label={searchAriaLabel}
					placeholder={searchPlaceholder}
					value={searchQuery}
					onChange={(e) => onSearchChange(e.target.value)}
					className="pl-9"
				/>
			</div>
			{items.length === 0 ? (
				<p className="py-4 text-center text-sm text-muted-foreground">
					{emptyMessage ?? `No items match "${searchQuery}"`}
				</p>
			) : (
				<div className="overflow-hidden rounded-lg border">
					{items.map((item, index) => (
						<button
							key={item.key}
							type="button"
							className={
								"flex w-full items-center gap-3 bg-card px-4 py-3 text-left text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground" +
								(index === items.length - 1 ? "" : " border-b")
							}
							onClick={() => onSelect(item)}
						>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									{renderTitle ? (
										renderTitle(item)
									) : (
										<span className="text-sm font-medium text-foreground">
											{item.name}
										</span>
									)}
									<CatalogBadges badges={item.badges} />
								</div>
								{item.description ? (
									<p className="line-clamp-1 text-xs text-muted-foreground">
										{item.description}
									</p>
								) : null}
							</div>
							<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
						</button>
					))}
				</div>
			)}
		</div>
	);
}
